import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import api from '../../../services/api';

const useCafeteriaPoolApi = (queryParams) => {
  const { t } = useTranslation();

  const [fetching, setFetching] = useState(true);
  const [data, setData] = useState([]);
  const [pagination, setPagination] = useState({ totalRowCount: 0, pageSize: 10, pageIndex: 0 });
  const [refreshTrigger, setRefreshTrigger] = useState(0);
  const [periods, setPeriods] = useState([]);
  const lastValidQueryParams = useRef(null);

  useEffect(() => {
    api.get('/v1/open-enrollment-periods?pageSize=200').then((res) => {
      setPeriods(res.data?.data?.data || []);
    }).catch(() => {});
  }, []);

  useEffect(() => {
    const paramsToUse = queryParams && Object.keys(queryParams).length > 0
      ? queryParams
      : lastValidQueryParams.current || { pageNumber: 1, pageSize: 10 };
    lastValidQueryParams.current = paramsToUse;
    setFetching(true);
    api.get(`/v1/cafeteria-pools?${new URLSearchParams(paramsToUse)}`)
      .then((res) => {
        const result = res.data?.data;
        const items = Array.isArray(result) ? result : (result?.data || []);
        setData(items);
        setPagination({
          totalRowCount: result?.totalRowCount || items.length,
          pageIndex: result?.pageIndex ? result.pageIndex - 1 : 0,
          pageSize: result?.pageSize || items.length || 10,
        });
      })
      .finally(() => setFetching(false));
  }, [queryParams, refreshTrigger]);

  const create = useCallback(async (formData) => {
    setFetching(true);
    try {
      // New pool starts with empty items — user adds them on details page
      await api.post('/v1/cafeteria-pools', { ...normalize(formData), items: [] });
      setRefreshTrigger((p) => p + 1);
      return true;
    } catch {
      setFetching(false);
      return false;
    }
  }, []);

  const update = useCallback(async (id, formData) => {
    setFetching(true);
    try {
      const current = await api.get(`/v1/cafeteria-pools/${id}`);
      const items = (current.data?.data?.items || []).map((it) => ({
        benefitId: it.benefitId,
        pointCost: it.pointCost,
        maxAllocationPerEmployee: it.maxAllocationPerEmployee,
        sortOrder: it.sortOrder ?? 0,
      }));
      await api.put(`/v1/cafeteria-pools/${id}`, { ...normalize(formData), items });
      setRefreshTrigger((p) => p + 1);
      return true;
    } catch {
      setFetching(false);
      return false;
    }
  }, []);

  const _delete = useCallback(async (ids) => {
    try {
      for (const id of ids) {
        await api.delete(`/v1/cafeteria-pools/${id}`);
      }
      setRefreshTrigger((p) => p + 1);
      return true;
    } catch {
      return false;
    }
  }, []);

  const columns = useMemo(() => [
    { name: 'id', label: 'ID', options: { filter: false, sort: false, display: 'excluded' } },
    { name: 'name', label: t('cafeteria.poolName'), options: { filter: true, sort: true, filterType: 'textField', filterOptions: { defaultOp: 'like' } } },
    { name: 'openEnrollmentPeriodName', label: t('cafeteria.period'), options: { filter: false, sort: false, customBodyRender: (v) => v ?? t('cafeteria.template') } },
    { name: 'itemCount', label: t('cafeteria.itemCount'), options: { filter: false, sort: false } },
  ], [t]);

  const objectSchema = useMemo(() => [
    { name: 'name', label: t('cafeteria.poolName'), type: 'text', grid: 12, required: true },
    { name: 'description', label: t('cafeteria.description'), type: 'textarea', grid: 12 },
    {
      name: 'openEnrollmentPeriodId', label: t('cafeteria.period'), type: 'select', grid: 12,
      options: periods, idKey: 'id', labelKey: 'name',
    },
  ], [t, periods]);

  return { objectSchema, columns, data, fetching, pagination, create, update, _delete };
};

function normalize(formData) {
  const out = { ...formData };
  if (out.openEnrollmentPeriodId === '') out.openEnrollmentPeriodId = null;
  return out;
}

export default useCafeteriaPoolApi;
